// src/hooks/useDashboardSummary.js
// Premium, production-grade data hook for the Dashboard.
// - Time-only filters (range OR from/to) + tz
// - Stable queryKey (delegates normalization to the service)
// - Sensible React Query defaults
// - Optional URL sync with tiny helpers
// NOTE: The server/service now returns `teamWorkload`; it’s available at `query.data.teamWorkload`.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { getDashboardSummary, toSearchParams } from '../services/dashboardService';
import queryClient from '../lib/queryClient';

/* =============================================================================
   CONSTANTS / HELPERS
============================================================================= */

const PARAM_KEYS = ['from', 'to', 'range', 'tz'];

// Keep in sync with the service’s normalization
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
// UI uses 3/6/12/24m, but allow 1–36 months to stay future-proof
const RANGE_RE = /^\d{1,2}m$/i;

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

function userTZ() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

function clean(obj = {}) {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) =>
        v !== undefined &&
        v !== null &&
        v !== '' &&
        !(typeof v === 'number' && Number.isNaN(v))
    )
  );
}

/**
 * Normalize a raw params object to a *stable* plain object for query keys.
 * Mirrors the service’s normalization closely (time-only).
 */
function normalizeParamsForKey(raw = {}) {
  const p = {};
  // Prefer explicit dates over range
  if (raw.from || raw.to) {
    if (raw.from && ISO_DATE_RE.test(raw.from)) p.from = raw.from;
    if (raw.to && ISO_DATE_RE.test(raw.to)) p.to = raw.to;
  } else if (raw.range && RANGE_RE.test(raw.range)) {
    const m = raw.range.match(/^(\d{1,2})m$/i);
    if (m) {
      const n = clamp(parseInt(m[1], 10), 1, 36);
      p.range = `${n}m`;
    }
  } else {
    p.range = '12m';
  }

  p.tz = raw.tz || userTZ();
  return clean(p);
}

/* =============================================================================
   URL-SYNCED FILTERS (optional)
============================================================================= */

/**
 * useDashboardFilters
 * Keeps local filter state and (optionally) syncs it to the URL.
 * - Ensures `range` is mutually exclusive with `from/to`.
 * - Returns [params, setParams, helpers].
 */
export function useDashboardFilters({ urlSync = true, initial = {} } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();

  // read from URL on mount (only these keys)
  const urlParams = useMemo(() => {
    const obj = {};
    PARAM_KEYS.forEach((k) => {
      const v = searchParams.get(k);
      if (v != null) obj[k] = v;
    });
    return obj;
  }, [searchParams]);

  // initial composed from URL (priority) then initial prop
  const [params, _setParams] = useState(() => {
    const merged = { tz: userTZ(), range: '12m', ...initial, ...urlParams };
    const p = { ...merged };

    // sanitize dates
    if (p.from && !ISO_DATE_RE.test(p.from)) delete p.from;
    if (p.to && !ISO_DATE_RE.test(p.to)) delete p.to;

    // exclusivity: if either from/to is present, drop range; else ensure range exists
    if (p.from || p.to) delete p.range;
    if (!p.range && !p.from && !p.to) p.range = '12m';

    return p;
  });

  const lastSyncedRef = useRef('');

  // safe setter that enforces from/to vs range exclusivity
  const setParams = useCallback((update) => {
    _setParams((prev) => {
      const next = typeof update === 'function' ? update(prev) : { ...prev, ...update };
      const out = { ...next };

      // sanitize dates
      if (out.from && !ISO_DATE_RE.test(out.from)) delete out.from;
      if (out.to && !ISO_DATE_RE.test(out.to)) delete out.to;

      // exclusivity
      if (out.from || out.to) {
        delete out.range;
      } else if (!out.range) {
        out.range = '12m';
      }

      // default tz
      if (!out.tz) out.tz = userTZ();

      return out;
    });
  }, []);

  // URL sync (optional)
  useEffect(() => {
    if (!urlSync) return;
    const normalized = normalizeParamsForKey(params);
    const qs = new URLSearchParams(normalized);
    const str = qs.toString();
    if (str !== lastSyncedRef.current) {
      setSearchParams(qs, { replace: true });
      lastSyncedRef.current = str;
    }
  }, [params, setSearchParams, urlSync]);

  const helpers = useMemo(() => {
    return {
      resetToLast12Months: () => setParams({ range: '12m', from: undefined, to: undefined }),
      setDateRange: (from, to) => setParams({ from, to, range: undefined }),
      setQuickMonths: (months) =>
        setParams({ range: `${clamp(Number(months || 12), 1, 36)}m`, from: undefined, to: undefined }),
      setTZ: (tz) => setParams({ tz }),
    };
  }, [setParams]);

  return [params, setParams, helpers];
}

/* =============================================================================
   DATA HOOK
============================================================================= */

/**
 * useDashboardSummary
 * Fetches the server-computed dashboard summary.
 *
 * @param {Object} options
 * @param {Object} options.params                 - time filters ({ from, to, range, tz })
 * @param {boolean} [options.enabled=true]        - enable/disable fetching
 * @param {number}  [options.staleTime=30000]     - ms; how long data stays fresh
 * @param {number}  [options.cacheTime=300000]    - ms; how long inactive cache kept
 * @param {number}  [options.retry=1]             - react-query retries
 * @param {boolean} [options.keepPreviousData=true]
 * @param {boolean} [options.refetchOnWindowFocus=false]
 * @param {AbortSignal} [options.signal]
 * @returns react-query result + helpers ({ refresh, queryKeyStr })
 */
export function useDashboardSummary({
  params = {},
  enabled = true,
  staleTime = 30_000,
  cacheTime = 5 * 60_000,
  retry = 1,
  keepPreviousData = true,
  refetchOnWindowFocus = false,
  signal,
} = {}) {
  const qc = useQueryClient() || queryClient;

  // Stable, normalized key (stringified search params)
  const queryKeyStr = useMemo(() => toSearchParams(params), [params]);
  const queryKey = useMemo(() => ['dashboard.summary', queryKeyStr], [queryKeyStr]);

  const query = useQuery({
    queryKey,
    queryFn: () => getDashboardSummary({ ...params, signal }),
    enabled,
    staleTime,
    cacheTime,
    retry,
    keepPreviousData,
    refetchOnWindowFocus,
    meta: { feature: 'dashboard', scope: 'summary' },
  });

  const refresh = useCallback(() => {
    return qc.invalidateQueries({ queryKey });
  }, [qc, queryKey]);

  const ready = !!query.data && !query.isFetching && !query.isError;

  return {
    ...query,
    ready,
    refresh,
    queryKeyStr,
  };
}

/* =============================================================================
   CONVENIENCE COMPOSITE HOOK (optional)
============================================================================= */

/**
 * useDashboard (one-stop convenience)
 * - Manages URL-synced filters
 * - Fetches summary data
 */
export function useDashboard({
  urlSync = true,
  initialFilters = {},
  queryOptions = {},
} = {}) {
  const [params, setParams, filtersHelpers] = useDashboardFilters({
    urlSync,
    initial: initialFilters,
  });
  const summary = useDashboardSummary({ params, ...queryOptions });

  return {
    params,
    setParams,
    filtersHelpers,
    summary,
  };
}

export default {
  useDashboardFilters,
  useDashboardSummary,
  useDashboard,
};
